import { useCallback, useMemo, useState } from "react";
import { isOpenReview, useCellReviews } from "@platform/plan-grid-core";

import {
  createPlanRateValidationMenuItems,
  createRateReviewMenuItems,
} from "../components/RatesGrid/rateReviewMenu";

import type { RateReviewTarget } from "../utils/rateReviewTarget";
import type {
  CellReview,
  CommentDialogProps,
  ValidationMenuCallbacks,
} from "@platform/plan-grid-core";
import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
} from "ag-grid-community";

/**
 * Which write the dialog's Save performs. A review carries the flag with the
 * text; on a validated cell only the text changes, and the cell stays validated.
 */
type DialogMode = "review" | "comment";

interface DialogTarget {
  target: RateReviewTarget;
  mode: DialogMode;
  existingReview?: CellReview;
}

const MENU_SEPARATOR: DefaultMenuItem = "separator";

const DIALOG_TITLES: Record<DialogMode, (hasReview: boolean) => string> = {
  review: (hasReview) => (hasReview ? "Edit/View Review" : "Needs Review"),
  comment: () => "Edit/Review Comment",
};

export interface RateReviewMenu {
  getContextMenuItems: (
    params: GetContextMenuItemsParams,
  ) => (DefaultMenuItem | MenuItemDef)[];
  commentDialogProps: CommentDialogProps;
}

/**
 * Wires the rates grid's right-click review actions to whichever cell-review
 * store the surrounding surface mounted, and owns the comment dialog they open.
 *
 * The dialog is held here rather than in the shared `useDialogs` store because
 * the Build tab mounts the rates grid outside the plan designer's DialogProvider.
 */
export const useRateReviewMenu = (
  enableCellValidation = false,
): RateReviewMenu => {
  const {
    getReview,
    upsertReview,
    clearReview,
    setValidated,
    setComment,
    commentMaxLength,
  } = useCellReviews();
  const [dialogTarget, setDialogTarget] = useState<DialogTarget | null>(null);

  const closeDialog = useCallback(() => setDialogTarget(null), []);

  const validationCallbacks = useMemo<ValidationMenuCallbacks>(
    () => ({
      isValidated: (planId, fieldPath) =>
        Boolean(getReview(planId, fieldPath)?.isValidated),
      getComment: (planId, fieldPath) =>
        getReview(planId, fieldPath)?.comment ?? "",
      onSetValidated: setValidated,
      onEditComment: (planId, fieldPath) =>
        setDialogTarget({
          target: { planId, fieldPath },
          mode: "comment",
          existingReview: getReview(planId, fieldPath),
        }),
      onClearComment: (planId, fieldPath) => setComment(planId, fieldPath, ""),
    }),
    [getReview, setValidated, setComment],
  );

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => {
      const reviewItems = createRateReviewMenuItems(
        params.event?.target ?? null,
        {
          getReview,
          onOpenReview: (target, existingReview) =>
            setDialogTarget({ target, mode: "review", existingReview }),
          onClearReview: ({ planId, fieldPath }) =>
            clearReview(planId, fieldPath),
        },
        enableCellValidation ? validationCallbacks : undefined,
      );

      if (reviewItems.length > 0) {
        return reviewItems;
      }

      const defaultItems = params.defaultItems ?? [];
      const planItems = enableCellValidation
        ? createPlanRateValidationMenuItems(
            params.event?.target ?? null,
            validationCallbacks,
          )
        : [];

      return planItems.length > 0 && defaultItems.length > 0
        ? [...planItems, MENU_SEPARATOR, ...defaultItems]
        : [...planItems, ...defaultItems];
    },
    [getReview, clearReview, enableCellValidation, validationCallbacks],
  );

  const commentDialogProps = useMemo<CommentDialogProps>(() => {
    const mode = dialogTarget?.mode ?? "review";
    const hasReview = isOpenReview(dialogTarget?.existingReview);

    return {
      open: dialogTarget !== null,
      title: DIALOG_TITLES[mode](hasReview),
      currentComment: dialogTarget?.existingReview?.comment ?? "",
      createdAt: dialogTarget?.existingReview?.createdAt,
      updatedAt: dialogTarget?.existingReview?.updatedAt,
      maxLength: commentMaxLength,
      onSubmit: (comment: string) => {
        if (dialogTarget) {
          const { planId, fieldPath } = dialogTarget.target;
          if (dialogTarget.mode === "comment") {
            setComment(planId, fieldPath, comment);
          } else {
            upsertReview(planId, fieldPath, comment || null);
          }
        }
        closeDialog();
      },
      onCancel: closeDialog,
    };
  }, [dialogTarget, commentMaxLength, upsertReview, setComment, closeDialog]);

  return { getContextMenuItems, commentDialogProps };
};
